import { z } from 'zod'

/** Rollen aus Konzept Kap. 3, ergänzt um `gast` (18.08.2026). */
export const PERSON_ROLES = ['entwickler', 'steuerer', 'mit_owner', 'admin', 'gast'] as const
export type PersonRole = (typeof PERSON_ROLES)[number]
export const personRoleSchema = z.enum(PERSON_ROLES)

export const PERSON_ROLE_LABELS: Record<PersonRole, string> = {
  entwickler: 'Entwickler:in',
  steuerer: 'Abteilungs-/Projektleitung',
  mit_owner: 'Mit-Owner (Fachbereich)',
  admin: 'Administration',
  gast: 'Gast (nur wo beteiligt)',
}

/** Ein Satz je Rolle – für die Benutzerverwaltung, wo die Wahl getroffen wird. */
export const PERSON_ROLE_HINWEISE: Record<PersonRole, string> = {
  entwickler: 'Zieht Aufgaben, arbeitet sie ab, zählt in Kapazität und Durchsatz.',
  steuerer: 'Sieht alles, steuert Prioritäten und Zuweisungen, sieht die Personen-Auswertung.',
  mit_owner: 'Fachbereich: stellt ein, schlägt Prioritäten vor, nimmt ab – sein Bereich.',
  admin: 'Zusätzlich Zugänge, Rollen, Regeln und KI-Verwaltung.',
  gast: 'Kein Teammitglied: sieht nur Aufgaben, an denen er beteiligt ist, und kann dort antworten.',
}

/**
 * **Die Rollen, die im Entwicklungsteam arbeiten** – alles außer dem Fachbereich.
 *
 * ## Warum die Liste andersherum steht als erwartet
 *
 * Man würde aufzählen, was ein Mit-Owner *darf*. Diese Liste zählt auf, wer **alles** darf –
 * und der Mit-Owner steht nicht drin. Der Unterschied zeigt sich beim nächsten Mal: wer eine
 * Prozedur hinzufügt und die Rollenprüfung vergisst, öffnet sie damit auch dem Fachbereich.
 * Bei einer Positivliste für Mit-Owner wäre es umgekehrt – und das ist die harmlosere
 * Richtung, weil eine zu enge Berechtigung sich meldet (jemand kann nicht arbeiten) und eine
 * zu weite nicht.
 *
 * Die verbindliche Prüfung ist `teamProcedure` im API-Paket. Hier steht sie, weil die
 * Oberfläche dieselbe Antwort braucht, um Navigation und Knöpfe wegzulassen.
 */
export const TEAM_ROLLEN: readonly PersonRole[] = ['entwickler', 'steuerer', 'admin']

/** Gehört die Rolle ins Entwicklungsteam – im Gegensatz zum Fachbereich? */
export function istTeam(role: PersonRole): boolean {
  return TEAM_ROLLEN.includes(role)
}

/**
 * **Rollen, die nebenbei mitentwickeln können** (Nils, 21.08.2026: *„Ich muss als Admin
 * gleichzeitig auch Entwickler sein – also dass ich in den Listen stehe."*).
 *
 * Eine Person hat **eine** Rolle, und das soll so bleiben: die Rolle beantwortet die Frage
 * „was darf jemand sehen und ändern", und daraus einen Mengensatz zu machen hieße, jede
 * Rechteprüfung im Haus umzuschreiben. Wer entwickelt, ist aber eine **andere** Frage – und
 * bei genau zwei Rollen fallen beide Antworten auseinander:
 *
 * - Die **Administration** vergibt Konten *und* zieht Aufgaben. Genau der Fall, der das hier
 *   ausgelöst hat: Nils stand in keiner Bearbeiter-Auswahl.
 * - Die **Abteilungsleitung** steuert *und* nimmt gelegentlich selbst etwas.
 *
 * Deshalb ein Häkchen neben der Rolle statt einer neuen Rolle „Admin, der auch entwickelt" –
 * die wäre in einem Jahr wieder zwei Rollen weiter. `entwickler` steht nicht in dieser Liste:
 * dort ist die Frage schon beantwortet.
 */
export const MITARBEIT_ROLLEN: readonly PersonRole[] = ['steuerer', 'admin']

/** Ist das Häkchen „arbeitet mit" für diese Rolle überhaupt eine Frage? */
export function kannMitarbeiten(role: PersonRole): boolean {
  return MITARBEIT_ROLLEN.includes(role)
}

/**
 * **Wer als Entwickler:in zählt** – der eine Begriff, den alle Auswahllisten und alle
 * Auswertungen benutzen ([E-199](../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Vorher stand an zwölf Stellen `role === 'entwickler'` – in der Bearbeiter-Auswahl, in
 * „Kommen in Frage", in der Triage, in der Kapazität, im abgeleiteten Profil. Ein Häkchen,
 * das nur elf davon erreicht, ist schlimmer als keines: die Person stünde in der Auswahl,
 * fiele aber aus der Auswertung – und niemand fände heraus, warum ihre Aufgaben nirgends
 * auftauchen.
 *
 * ## Warum die Rolle trotzdem geprüft wird
 *
 * `arbeitetMit` allein würde genügen, solange niemand die Rolle wechselt. Wird ein Admin mit
 * gesetztem Häkchen zum `gast` herabgestuft, bliebe das Häkchen stehen – und ein Gast stünde
 * in der Entwicklerliste. Die Prüfung auf {@link kannMitarbeiten} macht den Rollenwechsel zur
 * Wahrheit, ohne dass ihn jemand nachpflegen muss.
 */
export function zaehltAlsEntwickler(person: { role: PersonRole; arbeitetMit: boolean }): boolean {
  return person.role === 'entwickler' || (person.arbeitetMit && kannMitarbeiten(person.role))
}

/**
 * **Mit-Owner: Fachbereich, kein Entwickler** (Konzept Kap. 5.8, Phase 9).
 *
 * Er stellt Aufgaben ein, schlägt eine Priorität vor und nimmt ab. Er zieht nichts, weist
 * niemandem etwas zu und ändert keine Aufgaben – das ist die ganze Rolle.
 */
export function istMitOwner(role: PersonRole): boolean {
  return role === 'mit_owner'
}

/**
 * **Gast: ein Name, kein Teammitglied** (Nils, 18.08.2026: *„eine Rolle, wo ich die zwar
 * nicht deaktiviere, sondern runterstufe – die ich nach wie vor sehe, wenn die Kommentare
 * schreiben, die ich mit @ markieren kann, die Mails bekommen, die keine Entwickler sind."*).
 *
 * ## Warum das keine Deaktivierung sein darf
 *
 * `isActive = false` heißt „ausgeschieden": die Person fällt aus der Auswertung, aus der
 * Kapazität – und aus `people.all`, also auch aus der @-Auswahl und dem Mailversand
 * ([E-130](../../../docs/ENTSCHEIDUNGEN.md)). Wer bloß kein Entwickler ist, wäre damit
 * unansprechbar. Das ist eine Rollenfrage, keine Zustandsfrage.
 *
 * ## Was ein Gast ist
 *
 * | ohne Konto | mit Konto |
 * |---|---|
 * | ein **Name**: steht im Verlauf, in Kommentaren, in der Zeitleiste | zusätzlich: meldet sich an, sieht **nur** seine Beteiligungen, kann dort antworten |
 *
 * In keiner Entwickler-Auswahl, in keiner Kapazität, in keinem Durchsatz. Die
 * Sichtbarkeitsregel steht in `apps/api/src/trpc/rechte.ts`, die schreibenden Grenzen in
 * `mitarbeitProcedure`.
 */
export function istGast(role: PersonRole): boolean {
  return role === 'gast'
}

/**
 * **Wer nur einen Ausschnitt des Bestands sieht** – Mit-Owner und Gast.
 *
 * Die eine Stelle, an der beide zusammenfallen, und deshalb eine eigene Funktion: `rechte.ts`
 * fragte bis zum 18.08.2026 `istMitOwner`, und **jede** dieser Abfragen hätte einem Gast den
 * ganzen Bestand gezeigt. Ein vergessener Sichtbarkeitsfilter meldet sich nie von selbst –
 * die Liste ist dann nur länger, als sie sein dürfte.
 */
export function hatEingeschraenkteSicht(role: PersonRole): boolean {
  return istMitOwner(role) || istGast(role)
}

/**
 * Standard-WIP-Limit: wie viele Aufgaben gleichzeitig „In Arbeit" sein dürfen (Kap. 4.3).
 *
 * **10, nicht 3** (14.09.2026, [F-15](../../../docs/OFFENE-FRAGEN.md)). Das Konzept schlug 3
 * vor; gemessen liefen 8–9 je Person, weil „Status in Arbeit" und „arbeitet daran" in GitLab
 * nicht dasselbe sind. Nils: *„Standard 10."* Wer enger führen will, setzt es je Person oder je
 * Arbeitswelt herunter – die Zahl ist ein Deckel gegen Wildwuchs, kein Taktgeber.
 */
export const DEFAULT_WIP_LIMIT = 10
